/**
 * Immutable, tab-scoped paid request identity. Storage is mandatory: an
 * unreadable record is NOT evidence that no request exists.
 *
 * @typedef {"prepare"|"add"|"touchups"|"video"|"regenerate"} PaidAction
 * @typedef {{action: PaidAction, targetUuid: string, artifact?: string}} PaidScope
 * @typedef {{schema:1, attempt_id:string, action:PaidAction, target_uuid:string,
 * artifact:string|null, request:Record<string,any>, created_at:number}} PaidAttempt
 * @typedef {{attempt:Record<string,any>, job:any, settlement:Record<string,any>,
 * video_preview:any, replacement_jobs:any[], replacement_chain_complete:boolean}} PaidReceipt
 */

const ACTIONS = ["prepare", "add", "touchups", "video", "regenerate"];
const ARTIFACTS = ["branded_pdf", "deck", "one_pager", "social_set"];
const UUID4 = /^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i;
const TARGET = /^[A-Za-z0-9_-]{1,128}$/;
const PHOTO = /^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/;
const MAX_BYTES = 16384;
export const PAID_ATTEMPT_STORAGE_MESSAGE =
  "This browser could not safely save or read the purchase request. Enable session storage before starting. An existing request will not be replaced.";

export class PaidAttemptError extends Error {}

/** @param {any} value */
const object = (value) => !!value && typeof value === "object" && !Array.isArray(value);
/** @param {any} value */
const count = (value) => Number.isSafeInteger(value) && value >= 0;
/** @param {any} value */
const positive = (value) => count(value) && value > 0 && value <= 2147483647;
/** @param {any} value */
const identifier = (value) => typeof value === "string" && TARGET.test(value);
/** @param {any} value */
const nullableIdentifier = (value) => value === null || identifier(value);
/** @param {any} value */
const canonical = (value) => JSON.stringify(value, Object.keys(value).sort());
/** @param {Record<string,any>} value @param {string[]} keys */
const only = (value, keys) => Object.keys(value).every((key) => keys.includes(key));
/** @param {any} value @param {number} max @param {(item:any)=>boolean} valid */
const selection = (value, max, valid) => {
  if (!Array.isArray(value) || value.length > max || !value.every(valid))
    throw new PaidAttemptError("The purchase selection is invalid.");
  return [...new Set(value)].sort();
};

/** @param {PaidAction} action @param {any} request @returns {Record<string,any>} */
export const normalizePaidRequest = (action, request) => {
  if (!object(request)) throw new PaidAttemptError("The purchase request is invalid.");
  if (action === "prepare" && only(request, ["artifacts", "prep_template"])) {
    const artifacts = selection(request.artifacts, 4, (item) => ARTIFACTS.includes(item));
    if (!Object.prototype.hasOwnProperty.call(request, "prep_template")) return { artifacts };
    if (typeof request.prep_template === "string" && request.prep_template.length <= 120)
      return { artifacts, prep_template: request.prep_template.trim() };
  }
  if (action === "add" && only(request, ["artifact"]) && ARTIFACTS.includes(request.artifact))
    return { artifact: request.artifact };
  if (action === "touchups" && only(request, ["photos"])) {
    const photos = selection(
      request.photos,
      40,
      (item) => typeof item === "string" && PHOTO.test(item)
    );
    if (photos.length) return { photos };
  }
  if (
    action === "video" &&
    only(request, ["expected_version", "requested_clip_count", "acknowledge_human_review"]) &&
    positive(request.expected_version) &&
    positive(request.requested_clip_count) &&
    request.requested_clip_count <= 5 &&
    request.acknowledge_human_review === true
  )
    return {
      expected_version: request.expected_version,
      requested_clip_count: request.requested_clip_count,
      acknowledge_human_review: true,
    };
  if (
    action === "regenerate" &&
    only(request, ["base_version", "instruction"]) &&
    positive(request.base_version) &&
    (request.instruction === undefined || typeof request.instruction === "string")
  ) {
    const instruction = (request.instruction || "").trim();
    if (instruction.length <= 2000) return { base_version: request.base_version, instruction };
  }
  throw new PaidAttemptError("The purchase request is invalid.");
};

/** @param {PaidScope} scope */
export const paidAttemptKey = (scope) => {
  if (
    !ACTIONS.includes(scope.action) ||
    !identifier(scope.targetUuid) ||
    (scope.artifact !== undefined &&
      (scope.action !== "add" || !ARTIFACTS.includes(scope.artifact)))
  )
    throw new PaidAttemptError("The purchase target is invalid.");
  return `liq_paid_attempt:v1:${scope.action}:${scope.targetUuid}${scope.artifact ? `:${scope.artifact}` : ""}`;
};

/** @param {PaidScope} scope @returns {PaidAttempt|null} */
export const loadPaidAttempt = (scope) => {
  try {
    const raw = window.sessionStorage.getItem(paidAttemptKey(scope));
    if (raw === null) return null;
    if (!raw || raw.length > MAX_BYTES) throw new Error("Invalid record size");
    const record = JSON.parse(raw);
    if (
      !object(record) ||
      !only(record, [
        "schema",
        "attempt_id",
        "action",
        "target_uuid",
        "artifact",
        "request",
        "created_at",
      ]) ||
      record.schema !== 1 ||
      !UUID4.test(record.attempt_id) ||
      record.action !== scope.action ||
      record.target_uuid !== scope.targetUuid ||
      record.artifact !== (scope.artifact || null) ||
      !positiveTimestamp(record.created_at)
    )
      throw new Error("Invalid record identity");
    const normalized = normalizePaidRequest(record.action, record.request);
    if (
      canonical(normalized) !== canonical(record.request) ||
      (scope.artifact && record.request.artifact !== scope.artifact)
    )
      throw new Error("Invalid frozen request");
    return freezeRecord(record);
  } catch {
    throw new PaidAttemptError(PAID_ATTEMPT_STORAGE_MESSAGE);
  }
};
/** @param {any} value */
const positiveTimestamp = (value) => Number.isSafeInteger(value) && value > 0;

/** @param {PaidAttempt} record @returns {PaidAttempt} */
const freezeRecord = (record) => {
  for (const value of Object.values(record.request)) if (Array.isArray(value)) Object.freeze(value);
  Object.freeze(record.request);
  return Object.freeze(record);
};

/** @returns {string} */
const freshId = () => {
  if (!window.crypto?.getRandomValues)
    throw new PaidAttemptError(
      "This browser cannot securely identify a purchase. Use a current browser over HTTPS."
    );
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

/** @param {PaidScope} scope @param {any} request @returns {{attempt:PaidAttempt,created:boolean}} */
export const createPaidAttempt = (scope, request) => {
  const normalized = normalizePaidRequest(scope.action, request);
  if (scope.artifact && normalized.artifact !== scope.artifact)
    throw new PaidAttemptError("The purchase selection does not match its target.");
  const previous = loadPaidAttempt(scope);
  if (previous) {
    if (canonical(previous.request) !== canonical(normalized))
      throw new PaidAttemptError(
        "Check the existing purchase request before changing this selection."
      );
    return { attempt: previous, created: false };
  }
  /** @type {PaidAttempt} */
  const attempt = {
    schema: 1,
    attempt_id: freshId(),
    action: scope.action,
    target_uuid: scope.targetUuid,
    artifact: scope.artifact || null,
    request: normalized,
    created_at: Date.now(),
  };
  const serialized = JSON.stringify(attempt);
  try {
    if (serialized.length > MAX_BYTES) throw new Error("Record too large");
    window.sessionStorage.setItem(paidAttemptKey(scope), serialized);
    // Read-back is part of reservation. A swallowed/quota-blocked write cannot
    // authorize a paid POST, even if this tab could retain an in-memory copy.
    if (window.sessionStorage.getItem(paidAttemptKey(scope)) !== serialized)
      throw new Error("Reservation was not durable");
  } catch {
    throw new PaidAttemptError(PAID_ATTEMPT_STORAGE_MESSAGE);
  }
  return { attempt: freezeRecord(attempt), created: true };
};

/** @param {PaidScope} scope @param {string} expectedAttemptId */
export const clearPaidAttempt = (scope, expectedAttemptId) => {
  try {
    const current = loadPaidAttempt(scope);
    if (!current || current.attempt_id !== expectedAttemptId) return false;
    window.sessionStorage.removeItem(paidAttemptKey(scope));
    return window.sessionStorage.getItem(paidAttemptKey(scope)) === null;
  } catch {
    return false;
  }
};

/** @param {PaidAttempt} record */
export const paidAttemptEndpoint = (record) => {
  const id = encodeURIComponent(record.target_uuid);
  if (record.action === "prepare" || record.action === "add")
    return `/listings/${id}/${record.action}/`;
  return `/computations/${id}/${record.action === "video" ? "video-preview" : record.action}/`;
};

/** Validate exact identity before any callbacks or financial messages.
 * @param {any} value @param {PaidAttempt} record @returns {PaidReceipt}
 */
export const parsePaidReceipt = (value, record) => {
  const fail = () => {
    throw new PaidAttemptError(
      "The purchase status response could not be verified. Check its status again."
    );
  };
  if (!object(value) || !object(value.attempt) || !object(value.settlement)) return fail();
  const a = value.attempt;
  const s = value.settlement;
  if (
    a.version !== 1 ||
    a.attempt_id !== record.attempt_id ||
    a.action !== record.action ||
    !["accepted", "rejected"].includes(a.status) ||
    !nullableIdentifier(a.listing_uuid) ||
    !nullableIdentifier(a.computation_uuid) ||
    !nullableIdentifier(a.job_id) ||
    !nullableIdentifier(a.video_preview_id) ||
    (["prepare", "add"].includes(record.action) ? a.listing_uuid : a.computation_uuid) !==
      record.target_uuid
  )
    return fail();
  let request;
  try {
    request = normalizePaidRequest(record.action, a.request);
  } catch {
    return fail();
  }
  if (
    canonical(request) !== canonical(a.request) ||
    canonical(request) !== canonical(record.request)
  )
    return fail();
  if (
    ![
      a.charged_credits,
      a.free_redos_used,
      s.charged_credits,
      s.refunded_credits,
      s.free_redos_used,
    ].every(count) ||
    s.charged_credits !== a.charged_credits ||
    s.free_redos_used !== a.free_redos_used ||
    s.refunded_credits > s.charged_credits ||
    (s.free_redos_restored !== null &&
      (!count(s.free_redos_restored) || s.free_redos_restored > s.free_redos_used))
  )
    return fail();
  if (
    a.response !== null &&
    (!object(a.response) ||
      !Number.isInteger(a.response.status_code) ||
      a.response.status_code < 200 ||
      a.response.status_code > 599 ||
      !object(a.response.data))
  )
    return fail();
  if (
    a.status === "rejected" &&
    (a.response === null ||
      ![400, 402, 409, 422, 429, 503].includes(a.response.status_code) ||
      a.charged_credits !== 0 ||
      a.free_redos_used !== 0 ||
      a.job_id !== null ||
      a.video_preview_id !== null)
  )
    return fail();
  /** @param {any} job */
  const validJob = (job) =>
    object(job) &&
    identifier(job.job_id) &&
    ["queued", "processing", "completed", "failed"].includes(job.status) &&
    typeof job.mode === "string" &&
    (["generate", "touchups"].includes(job.mode)
      ? job.base_version === null || count(job.base_version)
      : positive(job.base_version));
  if (value.job !== null && (!validJob(value.job) || value.job.job_id !== a.job_id)) return fail();
  if (
    value.job?.computation_uuid !== undefined &&
    value.job.computation_uuid !== a.computation_uuid
  )
    return fail();
  if (
    value.video_preview !== null &&
    (!object(value.video_preview) ||
      value.video_preview.id !== a.video_preview_id ||
      !positive(value.video_preview.document_version) ||
      ![
        "queued",
        "processing",
        "awaiting_review",
        "approved",
        "rejected",
        "failed",
        "stale",
      ].includes(value.video_preview.status))
  )
    return fail();
  if (
    !Array.isArray(value.replacement_jobs) ||
    value.replacement_jobs.length > 5 ||
    typeof value.replacement_chain_complete !== "boolean"
  )
    return fail();
  let predecessor = a.job_id;
  const seen = new Set(predecessor ? [predecessor] : []);
  for (const job of value.replacement_jobs) {
    if (
      !predecessor ||
      !validJob(job) ||
      !identifier(job.computation_uuid) ||
      job.replaces_job_id !== predecessor ||
      seen.has(job.job_id)
    )
      return fail();
    seen.add(job.job_id);
    predecessor = job.job_id;
  }
  return value;
};

/** The only safe current job is the end of a complete, explicit chain.
 * @param {PaidReceipt} receipt */
export const paidAttemptCurrentJob = (receipt) =>
  receipt.replacement_chain_complete
    ? receipt.replacement_jobs[receipt.replacement_jobs.length - 1] || receipt.job
    : null;
